import { ICinnamonAction } from '../interfaces/actions'
import { isObject } from './validTypes'
import { makeObjectRecursion } from './recursions'
import processData from './processData'
import type { IGenericFunction, UnresolvedRecord } from '../interfaces'
import EventEmitter from '../classes/EventEmiter'
import { TemplateActionNames } from '../consts/defaultConfigValues/defaultConstants'
import { DEFERRED_ACTION_ARGS, splitDeferredArgs } from './deferredActionArgs'

/** no evaluate props */
const noEvaluate = ['styles', 'badge']

/**
 * Actions whose `actions` arg is a place-order queue (COM-1296). A queue entry
 * can be a `runStep` object, whose handlers must stay raw until the step
 * answers, so each entry is resolved as that action would be if it were
 * emitted on its own.
 */
const QUEUE_ACTIONS = new Set<string>([
  TemplateActionNames.PLACE_ORDER_FLOW,
  TemplateActionNames.SUMMARY_CONTINUE_OR_PLACE_ORDER,
])

/**
 * Resolves an action's args against `sources`, leaving its deferred args raw
 * (`DEFERRED_ACTION_ARGS`, which the config and render passes in
 * `processData` honour too).
 *
 * Exported for the after-order steps (COM-1296), which call `runStep` in code
 * rather than emitting it, so that their args resolve as an emitted one's
 * would.
 */
export const resolveActionArgs = (
  name: string,
  args: UnresolvedRecord,
  sources: UnresolvedRecord,
): UnresolvedRecord => {
  if (QUEUE_ACTIONS.has(name)) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return resolveQueueArgs(args, sources)
  }
  const deferredNames = DEFERRED_ACTION_ARGS.get(name)
  if (!deferredNames) {
    return processData(args, sources)
  }

  const { eager, deferred } = splitDeferredArgs(args, deferredNames)
  const resolvedArgs = { ...processData(eager, sources), ...deferred }
  return resolvedArgs
}

/**
 * A queue action's args (COM-1296), resolved as `processData` resolves them
 * except that a queued `runStep` keeps `onOutcome`, `onTimeout` and `onError`
 * raw, as it would if it were emitted on its own.
 *
 * Mirrors what `processData` does to an array, which `makeObjectRecursion`
 * defines: an object entry is resolved and anything else is left as it is,
 * so a string entry is untouched either way. An `actions` that is not an
 * array goes through `processData` with the rest, unchanged.
 */
const resolveQueueArgs = (
  args: UnresolvedRecord,
  sources: UnresolvedRecord,
): UnresolvedRecord => {
  const { actions, ...rest } = args
  if (!Array.isArray(actions)) {
    return processData(args, sources)
  }
  const resolvedActions = actions.map((entry: unknown) => {
    if (!isObject(entry)) {
      return entry
    }
    const action = entry as UnresolvedRecord
    const name = typeof action.name === 'string' ? action.name : ''
    if (!DEFERRED_ACTION_ARGS.has(name)) {
      return processData(action, sources)
    }
    const actionArgs = isObject(action.args)
      ? (action.args as UnresolvedRecord)
      : {}
    return { ...action, args: resolveActionArgs(name, actionArgs, sources) }
  })
  return { ...processData(rest, sources), actions: resolvedActions }
}

/**
 * Convert an object to an arrow function that performs other functions
 * @param actionsList: ICinnamonAction[]
 * @param actions: any
 * @returns unknown (value of the last action)
 */
function createFunction(
  actionsList: ICinnamonAction[],
  eventEmitter: EventEmitter,
  extraSources: UnresolvedRecord = {},
): (callbackArgs: any) => unknown {
  return (callbackArgs: any) => {
    /*
     * Components usually call back with an object, which spreads into the
     * `this` source unchanged. Some call back with a bare scalar instead —
     * OrderList's `onOpenDetail(id)` and `onFilterChange(status)` are the
     * first — and spreading a string yields a char-indexed object, so
     * `{{this}}` resolves to `{0:'o',1:'r',...}`. Wrapping a scalar as
     * `{ value }` makes it addressable as `{{this.value}}`.
     *
     * Deliberately narrow: null and undefined still spread to `{}` and every
     * object or array path is untouched, so no existing action changes.
     */
    const isScalarArg =
      callbackArgs !== null &&
      callbackArgs !== undefined &&
      typeof callbackArgs !== 'object'
    const compData = isScalarArg ? { value: callbackArgs } : { ...callbackArgs }
    // compData is the data that component returns when its handleAction is executed
    // `this` goes last, so an extra source can never stand in for it.
    const sources = {
      ...extraSources,
      this: compData,
    }
    // execute the array of actions
    return actionsList.reduce<unknown>((acc, { name, args = {} }) => {
      const newArgs = resolveActionArgs(name, args, sources) // process {{this}}} variables
      return eventEmitter.emit(name, newArgs)
    }, undefined)
  }
}

/**
 * A place-order list entry as `{ name, args }` (COM-1296). A string is a name
 * with no args, which is what every entry was before; anything else is
 * returned as it is, so an entry with no string `name` matches no action.
 */
export const toCinnamonAction = (
  entry: string | ICinnamonAction,
): ICinnamonAction => (typeof entry === 'string' ? { name: entry } : entry)

/**
 * Runs a list of authored actions from code, with `data` as their `this`.
 *
 * The same path a component's callback takes, so each entry's args are
 * resolved and emitted in order exactly as a click would resolve them — and a
 * `runStep` among them defers its own handlers again. For a hook that holds
 * actions to run later, such as a workflow step's `onOutcome`.
 *
 * A value that is not an array runs nothing, rather than throwing from inside
 * a poll callback: these lists come from remote JSON.
 *
 * `sources` adds sources beside `this` for the same single pass (COM-1296
 * review). A workflow step's handlers get `{ state }`, read from the store
 * when the list runs (`useWorkflowStep`'s `handlerSources`), and so do the
 * after-order entries: no earlier pass resolved either
 * (`DEFERRED_ACTION_ARGS`, and after-order lists never reach the render
 * pass), so without it `{{params.*}}`, `{{texts.*}}`, `{{globals.*}}` and
 * every other store-backed token would resolve to nothing. `this` is still
 * `data` whatever `sources` holds. Callers that pass nothing resolve against
 * `this` alone, as before.
 * @returns the value of the last action, as `createFunction` does
 */
export const runCinnamonActions = (
  actions: ICinnamonAction[],
  eventEmitter: EventEmitter,
  data: UnresolvedRecord,
  sources: UnresolvedRecord = {},
): unknown => {
  if (!Array.isArray(actions)) {
    return undefined
  }
  return createFunction(actions, eventEmitter, sources)(data)
}

/**
 * Check for cinnamon actions into an object
 * @param props: object
 * @param actions: any
 * @returns
 */
export const processActions = (
  props: UnresolvedRecord,
  eventEmitter: EventEmitter,
) => {
  const newProps: any = { ...props }

  Object.keys(newProps).forEach((prop: string) => {
    // if prop is not into the noEvaluate list
    if (!noEvaluate.includes(prop)) {
      const propValue = newProps[prop]

      // if value is Object or Array
      if (typeof propValue === 'object') {
        let newPropValue: any = propValue

        // if is Object and contains 'cinnamonActions'
        if (isObject(propValue) && propValue.cinnamonActions) {
          const { cinnamonActions } = propValue
          newProps[prop] = createFunction(cinnamonActions, eventEmitter)
          return // skip to next prop
        }

        // recursion
        newPropValue = makeObjectRecursion(
          propValue,
          processActions as IGenericFunction,
          [eventEmitter],
        )

        // update prop
        newProps[prop] = newPropValue
      }
    }
  })

  return newProps
}

export default processActions
